// Response schemas for /packs (plan §4, spec §5.9 and J7): the dry-run diff an upload or a diff
// request returns, and the apply result. Shapes follow apps/api/src/modules/packs/service.ts and
// packages/content diff(); unknown keys are dropped.
import { z } from 'zod';

const MAX_ID = 200;
const MAX_TEXT = 2000;
const MAX_VERSION = 32;
const MAX_LIST = 20_000;
const MAX_STAMP = 64; // ISO-8601 instants and SHA-256 hex

const id = z.string().max(MAX_ID);
const text = z.string().max(MAX_TEXT);

export const PACK_STATUSES = ['validated', 'applied', 'failed'] as const;
export type PackStatus = (typeof PACK_STATUSES)[number];

export const packSummarySchema = z.object({
  version: z.string().min(1).max(MAX_VERSION),
  schemaVersion: z.string().max(MAX_VERSION),
  builtAt: z.string().max(MAX_STAMP),
  manifestSha256: z.string().max(MAX_STAMP),
  status: z.enum(PACK_STATUSES),
  importedAt: z.string().max(MAX_STAMP).nullable(),
});

export const diffEntrySchema = z.object({
  collection: id,
  id,
  fields: z.array(id).max(MAX_LIST).optional(),
});

export const blockerSchema = z.object({
  code: id,
  kind: id,
  id,
  message: text,
});

export const retiredSchema = z.object({
  kind: id,
  id,
  successor: id.nullable(),
  reason: text,
});

export const leadsPlanItemSchema = z.object({
  kind: id,
  key: id,
  action: id,
  fields: z.array(id).max(MAX_LIST),
});

export const packDiffSchema = z.object({
  pack: packSummarySchema,
  baseline: z.string().max(MAX_VERSION).nullable(),
  added: z.array(diffEntrySchema).max(MAX_LIST),
  changed: z.array(diffEntrySchema).max(MAX_LIST),
  removed: z.array(diffEntrySchema).max(MAX_LIST),
  retired: z.array(retiredSchema).max(MAX_LIST),
  blockers: z.array(blockerSchema).max(MAX_LIST),
  leads: z.array(leadsPlanItemSchema).max(MAX_LIST),
  canApply: z.boolean(),
});

export const packDiffEnvelopeSchema = z.object({ diff: packDiffSchema });

export const applyResultSchema = z.object({
  pack: packSummarySchema,
  rows: z.number().int().nonnegative(),
  retired: z.number().int().nonnegative(),
  leads: z.array(leadsPlanItemSchema).max(MAX_LIST),
});

// A 422 from the validator carries `details.issues` (each names the pack file it is about).
export const packIssueSchema = z.object({ code: id, path: text, message: text });
export const packIssuesSchema = z.array(packIssueSchema).max(MAX_LIST);

export type PackSummary = z.infer<typeof packSummarySchema>;
export type DiffEntry = z.infer<typeof diffEntrySchema>;
export type Blocker = z.infer<typeof blockerSchema>;
export type RetiredItem = z.infer<typeof retiredSchema>;
export type LeadsPlanItem = z.infer<typeof leadsPlanItemSchema>;
export type PackDiff = z.infer<typeof packDiffSchema>;
export type ApplyResult = z.infer<typeof applyResultSchema>;
export type PackIssue = z.infer<typeof packIssueSchema>;
